/* @refresh reload */
/**
 * Shared "+ New" control - empty desk, tab-bar split, folder context submenu rows.
 *
 * Gallery: /?test=maater-ui#create-in-folder
 * Actions: @/lib/create-in-folder
 */
import type { ReactNode } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CREATE_IN_FOLDER_ACTIONS,
  CREATE_IN_FOLDER_TRIGGER_LABEL,
  type CreateInFolderActionId,
} from '@/lib/create-in-folder'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'

const ITEM_ICON_CLASS = 'h-3.5 w-3.5'

export function CreateInFolderMenuItems({
  onAction,
  documentLabel,
  itemTestIds,
}: {
  onAction: (id: CreateInFolderActionId) => void
  /** Override the New document row (tab bar can say "New document in {folder}"). */
  documentLabel?: string
  itemTestIds?: Partial<Record<CreateInFolderActionId, string>>
}) {
  return (
    <>
      {CREATE_IN_FOLDER_ACTIONS.map((action) => {
        const Icon = action.icon
        const label =
          action.id === 'document' ? (documentLabel ?? action.label) : action.label
        return (
          <DropdownMenuItem
            key={action.id}
            onClick={() => onAction(action.id)}
            data-testid={itemTestIds?.[action.id]}
          >
            <Icon className={ITEM_ICON_CLASS} />
            {label}
          </DropdownMenuItem>
        )
      })}
    </>
  )
}

type CreateInFolderDropdownEmpty = {
  variant: 'empty'
  onAction: (id: CreateInFolderActionId) => void
  documentLabel?: string
  itemTestIds?: Partial<Record<CreateInFolderActionId, string>>
  triggerTestId?: string
}

type CreateInFolderDropdownTabBar = {
  variant: 'tab-bar'
  onAction: (id: CreateInFolderActionId) => void
  onPrimaryClick: () => void
  onChooseFolder?: () => void
  documentLabel?: string
  itemTestIds?: Partial<Record<CreateInFolderActionId, string>>
  primaryTestId?: string
  menuTestId?: string
  chooseFolderTestId?: string
  extraItems?: ReactNode
}

export type CreateInFolderDropdownProps =
  | CreateInFolderDropdownEmpty
  | CreateInFolderDropdownTabBar

export function CreateInFolderDropdown(props: CreateInFolderDropdownProps) {
  const menu = (
    <>
      <CreateInFolderMenuItems
        onAction={props.onAction}
        documentLabel={props.documentLabel}
        itemTestIds={props.itemTestIds}
      />
      {props.variant === 'tab-bar' && props.onChooseFolder ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={props.onChooseFolder}
            data-testid={props.chooseFolderTestId}
          >
            Choose folder...
          </DropdownMenuItem>
        </>
      ) : null}
      {props.variant === 'tab-bar' ? props.extraItems : null}
    </>
  )

  if (props.variant === 'empty') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
            data-testid={props.triggerTestId}
          >
            <Plus className="h-3.5 w-3.5" />
            {CREATE_IN_FOLDER_TRIGGER_LABEL}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
        >
          {menu}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="mb-0.5 flex h-7 items-stretch overflow-hidden rounded-md border border-transparent hover:border-border/70 hover:bg-muted/50">
      <button
        type="button"
        onClick={props.onPrimaryClick}
        className="inline-flex items-center gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        title="New document"
        aria-label="New document"
        data-testid={props.primaryTestId}
      >
        <Plus className="h-3.5 w-3.5" />
        {CREATE_IN_FOLDER_TRIGGER_LABEL}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center border-l border-border/50 px-1 text-muted-foreground hover:text-foreground transition-colors"
            title="More new options"
            aria-label="More new options"
            data-testid={props.menuTestId}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
        >
          {menu}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
